// Shapes of the api/2.0 guide payload the embed consumes. Only the fields the
// v5 embed actually reads are typed; the API returns more. Kept permissive
// (optional, index signature on Image) because the wire data is loosely shaped.

// Not exported: consumers reach it through the StepImage alias and the `image`
// fields below. (imgSrcSet, its former external importer, now takes the
// self-contained SizedImage from Guide/image_srcset.)
interface Image {
   id?: number;
   guid?: string;
   [sizeName: string]: string | number | undefined;
}

export interface Supply {
   text: string;
   url?: string;
   quantity: number;
   thumbnail?: string | null;
   notes?: string | null;
}

interface GuideDocument {
   text: string;
   url: string;
   icon?: string;
   extension?: string;
}

interface Flag {
   flagid: string;
   title: string;
   text: string;
}

interface Author {
   userid: number;
   username: string;
   url?: string;
   image?: Image | null;
}

export interface StepLine {
   text_rendered: string;
   text_raw?: string;
   bullet: string;
   level: number;
   lineid: number | null;
}

// Step images carry marker data too, but markers are a later slice of the port
// (image zoom + markers), so the field isn't modeled yet — a StepImage is just
// a sized image for now.
export type StepImage = Image;

interface StepVideoEncoding {
   url: string;
   mime: string;
}

export interface StepVideoMedia {
   encodings?: StepVideoEncoding[];
   image?: {
      image?: Image | null;
   } | null;
}

export interface StepEmbedMedia {
   html?: string;
}

interface StepMedia {
   type: 'image' | 'video' | 'embed' | string;
   data: StepImage[] | StepVideoMedia | StepEmbedMedia | Record<string, unknown>;
}

// A read-only step comment from the api/2.0 payload (stepComments=1). Only the
// fields the embed renders are typed; top-level comments carry `replies`.
export interface Comment {
   commentid: number;
   author?: Author | null;
   text_rendered?: string;
   date?: number;
   status?: 'public' | 'private' | 'deleted' | string;
   parentid?: number | null;
   replies?: Comment[];
}

export interface Step {
   stepid: number;
   orderby: number;
   title: string;
   lines: StepLine[];
   media: StepMedia;
   comments?: Comment[];
   comment_count?: number;
   // Added client-side (see api.ts amendGuide): 1-based position.
   number?: number;
   // Added client-side for video guides: [startTime, endTime] in seconds.
   videoTimestamp?: VideoTimestamp | null;
}

interface VideoTimestamp {
   startTime: number;
   endTime: number;
}

// Normalized intro video (see amendGuide): a single shape so consumers never
// sniff the api/2.0 `intro_video` wire union (null | hosted[] | oEmbed object).
export type IntroVideo =
   | { kind: 'hosted'; sources: { src: string; type: string }[] }
   | { kind: 'embed'; html: string };

export interface VideoInfo {
   type: 'none' | 'youtube' | 'ifixit' | string;
   videoId?: string;
   timestamps?: [number, VideoTimestamp][];
}

export interface Guide {
   guideid: number;
   title: string;
   url?: string;
   difficulty?: string;
   time_required?: string;
   introduction_rendered?: string;
   conclusion_rendered?: string;
   image?: Image | null;
   steps: Step[];
   flags: Flag[];
   tools: Supply[];
   parts: Supply[];
   documents: GuideDocument[];
   author: Author;
   comments?: unknown[];
   // GuideType wire value ('replacement', 'teardown', 'technique', ...). The
   // payload has no `teardown` boolean; teardown is `type === 'teardown'`.
   type?: string;
   featured_document_thumbnail_url?: string | null;
   featured_document_embed_url?: string | null;
   intro_video?: unknown;
   intro_video_url?: string | null;
   video_info?: VideoInfo | null;

   // Added client-side by amendGuide:
   introVideo?: IntroVideo | null;
   isVideoOnly?: boolean;
   isVideoGuide?: boolean;
}

/**
 * Whether the guide shows a Conclusion screen after its steps: only when it
 * rendered one — not on teardowns, not when the conclusion is empty. The step
 * navigation cap and the parent bridge's maxIndex both derive from this, so it
 * lives in one place to keep them from drifting.
 */
export function guideHasConclusion(guide: Guide): boolean {
   return guide.steps.length > 0 && guide.type !== 'teardown' && Boolean(guide.conclusion_rendered);
}

/**
 * Steps are the usual content but not the only content: a `technique` guide is
 * written as introduction prose and nothing else, and v4 rendered the title
 * screen and introduction for any guide. So gate on "nothing in any slot".
 */
export function isEmptyGuide(guide: Guide): boolean {
   return (
      guide.steps.length === 0 &&
      !guide.isVideoOnly &&
      !guide.introduction_rendered &&
      guide.tools.length === 0 &&
      guide.parts.length === 0 &&
      guide.documents.length === 0
   );
}
